import {
  findOtherOption,
  getModeTemplate,
  isOtherChosen,
  isOtherTextMissing,
  otherTextKey,
  readOtherText,
  type FieldDef,
} from '@citizen-walk/shared-types';
import type { DistrictSummary } from '../district';

/**
 * Post の extraFields renderer 用 pure helper (Sprint BG-DataSettings 5)。
 *
 * 旧 v1 `old/apps/user/src/pages/MainPage.tsx` post composer (L399-495 + L548-676) の
 * postFields 駆動描画ロジックから純粋部分のみを抽出。
 *
 * 旧との差分:
 *   - 旧は MainPage 内に inline で resolve していたが、PostExtraFieldsForm と
 *     test の両方から再利用するため module-scope helper に分離。
 *   - PostForm 本体側で扱う builtin field (title/comment/location/photo/category/detail)
 *     は extraFields renderer では skip する責務を `EXCLUDED_POST_FIELD_KEYS` で表現。
 */

/**
 * PostForm 本体が直接 handle する builtin field の key。
 * extraFields renderer (PostExtraFieldsForm) では重複描画を避けるため skip する。
 *
 * - `title` / `comment`: PostForm Card 2 / 4 で `<input>` / `<textarea>` 直書き
 * - `location` / `photo`: PostForm Card 1 / 3 で picker UI を直接 render
 * - `detail`: PostForm renderField の `case 'detail'` で textarea を直接 render
 *
 * **`category` は除外しない** (バグ修正): old2 PostTab (L25-26 / L361 `extraFields = {...extraValues}`)
 * では category は「組込以外の汎用フィールド」として renderGenericField で描画し extraFields に保存する。
 * v2 で誤って除外したため、防災モードの「投稿タイプ」(category) が PostForm の default case →
 * PostExtraFieldsForm → 本 set で再除外 → 何も描画されない、という回帰になっていた。category は
 * builtIn だが PostForm 本体に専用 UI を持たないため、他の select 同様 generic renderer に委ねる。
 */
export const EXCLUDED_POST_FIELD_KEYS: ReadonlySet<string> = new Set([
  'title',
  'comment',
  'location',
  'photo',
  'detail',
]);

/**
 * district.dataSettings.postFields を返す。未設定なら mode template の fallback。
 *
 * 旧 v1 では District に dataSettings が無い地区も存在したため、modeTemplates の
 * `getModeTemplate(mode).dataSettings.postFields` で常に値を保証する (admin が
 * Sprint BG-DataSettings で書き込む前の地区との後方互換)。
 *
 * 【廃止予定 fallback】getModeTemplate (@deprecated) 経路はモードドリブン化 (MD-4、2026-07-24)
 * 以降 legacy 地区専用 — 新規地区は ModeProfile 必須のため dataSettings.postFields が必ず
 * 転写済みで、この fallback には到達しない。custom mode の legacy 地区が dataSettings 未設定
 * だと BOUSAI_TEMPLATE に落ちる点に注意 (該当地区は dataSettings を設定して解消すること)。
 * 撤去条件: dataSettings 未設定の地区 0 件。
 */
export function resolvePostFields(district: DistrictSummary): FieldDef[] {
  const settings = district.dataSettings;
  if (settings && Array.isArray(settings.postFields) && settings.postFields.length > 0) {
    return settings.postFields;
  }
  return getModeTemplate(district.mode).dataSettings.postFields;
}

/**
 * PostExtraFieldsForm が描画対象とする postFields を絞り込む。
 *
 * 規則:
 *   1. `mode === 'hidden'` は skip (UserFieldsForm の `ordered` 算出と同 pattern)
 *   2. `EXCLUDED_POST_FIELD_KEYS` (PostForm 本体が handle する builtin) は skip
 *   3. `type === 'photo' / 'location'` も skip (本体側 UI と重複するため、旧 SurveyForm 同様)
 *   4. `displayOrder` 昇順 sort、未指定は 999 扱い (末尾)
 */
export function getRenderablePostFields(fields: FieldDef[]): FieldDef[] {
  return fields
    .filter((f) => f.mode !== 'hidden')
    .filter((f) => !EXCLUDED_POST_FIELD_KEYS.has(f.key))
    .filter((f) => f.type !== 'photo' && f.type !== 'location')
    .sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999));
}

/**
 * PostForm が「描画順を完全に admin 設定 (postFields.displayOrder) 駆動」にするための
 * **全可視 field** (組込 photo/title/location/comment + 地区独自 field を含む) を displayOrder 昇順で返す。
 *
 * old2 `apps/user/src/pages/tabs/PostTab.tsx` L56-62 `visibleFields` を逐語移植 (Phase E):
 *   - `mode !== 'hidden'` のみ残す
 *   - `displayOrder ?? Number.MAX_SAFE_INTEGER` の昇順、同値は元の配列 index で安定 sort
 *
 * `getRenderablePostFields` との違い: こちらは組込 (photo/title/location/comment) も **除外しない**。
 * PostForm が renderField switch で組込専用 UI を描き、それ以外を generic renderer へ委譲するため、
 * 描画ループの唯一の source となる。
 */
export function getVisiblePostFields(fields: FieldDef[]): FieldDef[] {
  return fields
    .filter((f) => f.mode !== 'hidden')
    .map((f, idx) => ({ f, order: f.displayOrder ?? Number.MAX_SAFE_INTEGER, idx }))
    .sort((a, b) => a.order - b.order || a.idx - b.idx)
    .map((x) => x.f);
}

/**
 * 必須 postField で未入力のものの label 配列を返す (空なら全て満たす)。
 *
 * 旧 v1 MainPage L548-580 の handlePost 内 validation 踏襲:
 *   - photo / location / 本体 handle 済 builtin (title/comment/category/detail) は skip
 *     (PostForm 本体の canSubmit 条件で別途 check するため)
 *   - hidden mode の field は required 扱いしない
 *   - 値が undefined / null / 空文字 / 空配列 のいずれかなら missing
 *   - rating-scale は数値 (0 含む) を許容、ただし `optionsMeta.excludeZero===true` のときは 0 を missing 扱い
 */
export function findMissingRequiredPostFields(
  fields: FieldDef[],
  values: Record<string, unknown>,
): string[] {
  const missing: string[] = [];
  for (const f of fields) {
    if (f.mode !== 'required') continue;
    if (f.mode === 'required' && EXCLUDED_POST_FIELD_KEYS.has(f.key)) continue;
    if (f.type === 'photo' || f.type === 'location') continue;
    const v = values[f.key];
    if (v === undefined || v === null) {
      missing.push(f.label);
      continue;
    }
    // 「その他」を選んだのに記入が空 (必須項目のみ)。
    if (isOtherTextMissing(f, values, v)) {
      missing.push(`${f.label}（${findOtherOption(f)?.label ?? 'その他'}の内容）`);
      continue;
    }
    if (typeof v === 'string' && v.trim() === '') {
      missing.push(f.label);
      continue;
    }
    if (Array.isArray(v) && v.length === 0) {
      missing.push(f.label);
      continue;
    }
    if (f.type === 'rating-scale') {
      if (typeof v !== 'number' || !Number.isFinite(v)) {
        missing.push(f.label);
        continue;
      }
      if (f.optionsMeta?.excludeZero === true && v === 0) {
        missing.push(f.label);
        continue;
      }
    }
  }
  return missing;
}

/**
 * 「その他」の記入内容を保存用に取り出す (2026-10-06、`fieldOtherText.ts`)。
 * 戻り値は `{ '{key}__other': 記入内容 | null }`。「その他」を選んでいない / 未記入なら null
 * (編集の保存では null で消す、新規投稿では null を書かない)。
 */
export function collectOtherTextValues(
  fields: FieldDef[],
  values: Record<string, unknown>,
): Record<string, string | null> {
  const out: Record<string, string | null> = {};
  for (const f of fields) {
    const other = findOtherOption(f);
    if (!other) continue;
    const text = readOtherText(values, f.key);
    out[otherTextKey(f.key)] = isOtherChosen(other.value, values[f.key]) && text ? text : null;
  }
  return out;
}
